<template>
  <div class="display-in-center landing-view-layout view" v-loading.fullscreen="loading" id="Register_Loading">
    <img alt="SmartBarcode logo" :src="image" class="app-logo" />

    <div id="Register_Form" class="register-form">
      <div id="Register_FieldInput" class="client-name field-input" :class="{ error: !!nameError }">
        <div id="Register_FirstName" class="first-name-input">
          <label>{{ $t('account.first_name') }}</label>
          <el-input id="Register_FirstNameInput" v-model="firstName" type="text" />
        </div>
        <div id="Register_LastName" class="last-name-input">
          <label>{{ $t('account.last_name') }}</label>
          <el-input id="Register_LastNameInput" v-model="lastName" type="text" />
        </div>
      </div>
      <p id="Register_NameError" v-if="nameError" class="error-msg">
        {{ nameError }}
      </p>

      <div v-if="isRequirePassword" class="client-password field-input" :class="{ error: !!passwordError }">
        <div id="Register_Password" class="password-input">
          <label> {{ $t('account.password') }}</label>
          <el-input id="Register_PasswordInput" v-model="password" type="password" />
        </div>

        <div id="Register_ConfirmPassword" class="password-input">
          <label>{{ $t('account.confirm_password') }}</label>
          <el-input id="Register_ConfirmPasswordInput" v-model="confirmPassword" type="password" />
        </div>
        <p v-if="passwordError" class="error-msg">{{ passwordError }}</p>
      </div>

      <el-button type="primary" circle class="go-button is-larger" @click="onSubmit">
        {{ $t('account.register') }}
      </el-button>
    </div>
  </div>

  <el-dialog
    width="90%"
    :lock-scroll="true"
    top="0"
    custom-class="register-success-modal"
    :model-value="registerSuccessDialog"
    :close-on-click-modal="false"
    :show-close="false"
    @closed="registerSuccessDialog = false"
  >
    <div class="register-success-dialog-message">
        {{ registerSuccessMessage }}
      </div>
    <template #footer>
      <div class="register-success-dialog-footer">
        <el-button
          id="Register_OK"
          type="primary"
          class="full-w-button register-success-ok-button"
          @click="onFinish"
        >
          <span class="register-success-ok-label">{{ $t('OK') }}</span>
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="onFinish"
            :visible="registerSuccessDialog"
            :newLayer="true"
          />
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'

import IconQRcode from '@/components/svg/IconQRcode.vue'
import IconScanQR from '@/components/svg/IconScanQR.vue'
import IconLogout from '@/components/svg/IconLogout.vue'
import BaseInput from '@/components/BaseInput.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'

import { register } from '@/utils/api'
import errorHandler from '@/utils/errorHandler'
import { FETCH_PROFILE } from '@/store/actions'
import { isEmpty } from '@/utils/typeChecker'
import { IRegisterReponse } from '@/utils/types/commonTypes'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'

@Options({
  components: {
    IconQRcode,
    IconScanQR,
    IconLogout,
    BaseInput,
    KeyenceHTHardKeyButton,
  },
  directives: { maska },
  name: 'Register',
})
export default class Register extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  loading = false
  registerSuccessDialog = false
  registerSuccessMessage = ''
  firstName = ''
  lastName = ''
  password = ''
  confirmPassword = ''

  nameError = ''
  passwordError = ''

  get image() {
    return this.$store.getters.customLogoImageUrl || require('@/assets/logo-smartBarcode.png')
  }

  get isAuth(): boolean {
    return this.$store.state.profile.user !== null
  }

  get projectParam() {
    return this.$store.getters.projectParam
  }

  get projectDetail() {
    return this.$store.state.project.details
  }

  get isValidName(): boolean {
    if (isEmpty(this.firstName)) {
      this.nameError = this.$t('account.first_name_not_empty')
      return false
    }

    if (isEmpty(this.lastName)) {
      this.nameError = this.$t('account.last_name_not_empty')
      return false
    }

    this.clearForm()
    return true
  }

  get isValidPassword(): boolean {
    if (this.isRequirePassword) {
      if (!this.password.match(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,32}$/g)) {
        this.passwordError = this.$t('account.password_violate_validation_rule')
        return false
      }

      if (this.password !== this.confirmPassword) {
        this.passwordError = this.$t('account.password_and_confirm_password_not_same')
        return false
      }
    }

    this.clearForm()
    return true
  }

  get isRequirePassword(): boolean {
    return this.projectDetail?.requirePassword
  }

  get isAllowUserSelfRegister(): boolean {
    return this.projectDetail?.allowUserSelfRegister
  }

  async onSubmit() {
    if (this.isValidName && this.isValidPassword) {
      this.loading = true
      await register({
        projectId: this.projectDetail?.id,
        firstName: this.firstName,
        lastName: this.lastName,
        ...(this.password && { password: this.password }),
      })
        .then((response) => {
          this.registerSuccessDialog = true
          const successResult: IRegisterReponse = { ...response.data }

          // success message
          this.registerSuccessMessage = this.$t('account.register_success_message').replace(
            '####-####',
            successResult.accessCode.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '-')
          )

          // Fetch user profile
          this.$store.dispatch(FETCH_PROFILE)
        })
        .catch((err) => {
          errorHandler(err)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  onFinish(): void {
    this.registerSuccessDialog = false
    this.$router.push({
      name: 'home',
      params: { project: this.projectParam },
    })
  }

  clearForm(): void {
    this.nameError = ''
    this.passwordError = ''
  }

  created() {
    if (!this.isAllowUserSelfRegister) {
      this.$router.push({ name: 'notFound' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';
.register-form {
  label {
    text-align: left;
    margin-bottom: 5px;
  }

  .client-name {
    display: flex;
    justify-content: space-between;
    align-items: left;

    .first-name-input {
      width: 47%;
      display: inline-block;
    }

    .last-name-input {
      width: 47%;
      display: inline-block;
    }
  }

  .client-password {
    .password-input {
      width: 100%;
      margin-top: 15px;
      display: inline-block;
    }
  }

  .go-button {
    margin: 40px auto 0;
  }

  .field-input {
    margin: 8px 0;

    &.error .el-input {
      border-color: $error;
    }

    .error-msg {
      color: $error;
      line-height: 1.2;
    }
  }
}

.register-success-dialog-message {
  margin: 0 4px 8px;
  line-height: 1.5;
  font-size: 16px;
}

::v-deep .register-success-modal {
  .el-dialog__footer {
    width: 100%;
    padding: 8px 4px 12px;
    box-sizing: border-box;
    text-align: stretch;
  }

  .register-success-dialog-footer {
    display: block;
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    }

  .register-success-ok-button.el-button {
    width: 100% !important;
    max-width: none;
    min-height: 64px;
    height: auto;
    margin: 0 !important;
    padding: 16px;
    box-sizing: border-box;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.3;
    position: relative;
    display: block !important;
  }

  .register-success-ok-label {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    pointer-events: none;
    line-height: 1.3;
    color: #ffffff;
    white-space: nowrap;
  }
}
</style>
